import { Box, Button, CircularProgress, FormHelperText, Stack, Typography } from "@mui/material";
import {
    ControlledTextField,
    Form,
    IconButton,
    Link,
    palette,
    PasswordStrengthIndicator,
    useHookForm,
} from "@powerdiary/components-library/lib";
import type { ZXCVBNResult } from "zxcvbn";
import { BaseSyntheticEvent, useCallback, useState } from "react";
import { Controller } from "react-hook-form";
import { useClientPortalConfigContext } from "../../../../context";
import { LoginDialogMode } from "../../Account.types";
import { generateDefaultValues, getButtonTitle, registerSchema, RegisterType, Step } from "./ResetPasswordForm.helpers";
import { useResetPassword } from "./useResetPassword";
import { Eye, EyeOff } from "lucide-react";
import { VerificationCodeInput } from "../../../common/VerificationCodeInput";

interface IRegisterFormProps {
    setMode: (mode: LoginDialogMode) => void;
}

export default function ResetPasswordForm({ setMode }: IRegisterFormProps) {
    const [showPassword, setShowPassword] = useState(false);
    const { clientPortalConfig } = useClientPortalConfigContext();

    const isSmsType = clientPortalConfig.tokenVia === "sms";

    const form = useHookForm({
        defaultValues: generateDefaultValues(clientPortalConfig.countryCode, isSmsType),
        schema: registerSchema,
        mode: "onChange",
    });
    const { formState, getValues, watch, setValue, trigger, control } = form;
    const verificationStep = getValues("step");
    const { register, isPending, resendVerificationCode, isResending, sentViaEmail } = useResetPassword(form, setMode);

    const password = watch("password") || "";

    const handleResult = useCallback(
        (result?: ZXCVBNResult) => {
            if (!result) return;

            const prevScore = getValues("passwordScore");

            setValue("passwordScore", result?.score || 0);
            if (prevScore !== result?.score) {
                trigger("password").catch(() => {});
            }
        },
        [getValues, setValue, trigger]
    );

    const handleRegister = async (values: RegisterType, event: BaseSyntheticEvent) => {
        await register(values, event);
    };

    return (
        <Form form={form} onSubmit={handleRegister} autoComplete="off" dataTestIdSuffix={"recover"}>
            <Stack gap="16px">
                {isPending ? (
                    <CircularProgress sx={{ alignSelf: "center" }} />
                ) : (
                    <>
                        {verificationStep === Step.Initial && (
                            <>
                                <Typography variant="paragraphSmallRegular" data-test-id="text_verification_code_info">
                                    Enter your details to receive a verification code.
                                </Typography>
                                <ControlledTextField dataTestIdSuffix="firstName" label="First Name" name="firstName" />
                                <ControlledTextField dataTestIdSuffix="lastName" label="Last Name" name="lastName" />
                                {isSmsType && (
                                    <ControlledTextField
                                        dataTestIdSuffix="mobileNumber"
                                        label="Mobile Number"
                                        name="phoneNumber"
                                    />
                                )}
                                <ControlledTextField dataTestIdSuffix="email" type="email" label="Email" name="email" />
                            </>
                        )}

                        {verificationStep === Step.Verification && (
                            <Stack gap={"12px"}>
                                <Typography variant="paragraphLargeBold">Enter Verification Code</Typography>
                                <Typography variant="paragraphSmallRegular">
                                    Please enter the activation code received.
                                </Typography>
                                {sentViaEmail && (
                                    <Typography variant="paragraphSmallRegular" data-test-id="text_sent_via_email_info">
                                        Verification code sent to email.
                                    </Typography>
                                )}
                                <Controller
                                    name="verificationCode"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <VerificationCodeInput
                                            value={field.value || ""}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            dataTestIdPrefix="verificationCode"
                                        />
                                    )}
                                />
                                <Typography variant="paragraphSmallRegular">
                                    {`Didn't receive the verification code?`}
                                </Typography>
                                <Link
                                    variant="paragraphSmallRegular"
                                    data-test-id="resend_verification_code"
                                    onClick={() => void resendVerificationCode()}
                                    sx={{
                                        cursor: isResending ? "wait" : "pointer",
                                        pointerEvents: isResending ? "none" : "auto",
                                        opacity: isResending ? 0.6 : 1,
                                    }}
                                >
                                    {isResending ? "Resending..." : "Resend the verification code"}
                                </Link>
                            </Stack>
                        )}
                        {verificationStep === Step.PasswordReset && (
                            <>
                                <ControlledTextField
                                    dataTestIdSuffix="password"
                                    type={showPassword ? "text" : "password"}
                                    InputProps={{
                                        autoComplete: "new-password",
                                        endAdornment: (
                                            <IconButton
                                                variant="translucent"
                                                size="extraSmall"
                                                iconColor={palette.neutral["900"]}
                                                icon={showPassword ? EyeOff : Eye}
                                                onClickHandler={() => setShowPassword((prev) => !prev)}
                                                data-test-id="button_toggle_password_visibility"
                                                title={showPassword ? "Hide password" : "Show password"}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                boxStyles={{ marginRight: "-8px" }}
                                            />
                                        ),
                                    }}
                                    label="New Password"
                                    name="password"
                                />
                                <PasswordStrengthIndicator
                                    password={password}
                                    handlePasswordValidation={handleResult}
                                    sx={{
                                        marginTop: "-12px",
                                    }}
                                    customLabels={{
                                        0: "Very weak - strong password required",
                                        1: "Weak - strong password required",
                                    }}
                                />
                            </>
                        )}
                        {formState?.errors?.root && (
                            <FormHelperText error data-test-id="form_error_root_message">
                                <>{formState.errors.root.message}</>
                            </FormHelperText>
                        )}
                    </>
                )}
                <Button fullWidth type="submit" variant="contained" disabled={isPending} data-test-id="button_register">
                    {getButtonTitle(verificationStep)}
                </Button>
                {verificationStep === Step.Initial && (
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "16px" }}>
                        <Typography variant="paragraphSmallRegular" textAlign="center">
                            Already registered?{" "}
                            <Link data-test-id="link_login" onClick={() => setMode(LoginDialogMode.Login)}>
                                Log in here
                            </Link>
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Form>
    );
}
